import SearchIcon from "@mui/icons-material/Search"
import { Autocomplete, CircularProgress, InputAdornment, TextField } from "@mui/material"
import { styled } from "@mui/material/styles"
import { debounce } from "lodash"
import PropTypes from "prop-types"
import React, { useCallback, useEffect, useMemo, useState } from "react"

import { apiClient } from "@/api"
import withAlerter from "../common/withAlerter"
import FormFieldErrorFeedback from "./FormFieldErrorFeedback"

const StyledAutocomplete = styled(Autocomplete)(({ theme, hasvalue, error }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "9999px !important",
    padding: "0.3rem 0.5rem",
    transition: theme.transitions.create(["border-color", "background-color", "box-shadow"]),
    backgroundColor: hasvalue ? theme.palette.grey[50] : theme.palette.background.paper,
    borderColor: error ? theme.palette.error.main : hasvalue ? theme.palette.primary.main : theme.palette.grey[300],
    "&.Mui-focused": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-disabled": {
      backgroundColor: theme.palette.action.disabledBackground,
      cursor: "not-allowed",
    },
  },
}))

const MuiAsyncAutocomplete = ({
  alerter,
  className,
  source,
  dynamicSource,
  placeholder,
  fieldName,
  selected,
  validationErrors,
  disabled,
  onSelect,
  formIdToAutoSubmit,
  hideError,
  isMulti,
  clearAfterSelect = false,
  externalParams,
  defaultOptions = false,
  queryParam = "q",
  omitEmptyQuery = false,
  startAdornment,
  showAdornment = false,
}) => {
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [value, setValue] = useState(selected)
  const [inputValue, setInputValue] = useState("")
  const [lastQuery, setLastQuery] = useState(null)

  useEffect(() => {
    setValue(selected)
  }, [selected])

  const fetchOptions = useCallback(
    async query => {
      // Don't fetch if:
      // 1. No query and defaultOptions is false
      // 2. Query is the same as last query (prevents duplicate requests)
      // 3. Query is empty and we should omit empty queries
      if ((!query && !defaultOptions) || query === lastQuery || (omitEmptyQuery && !query)) {
        return []
      }

      setLoading(true)
      setLastQuery(query)

      try {
        if (typeof dynamicSource === "function") {
          const results = await dynamicSource(query)
          setOptions(results)
          return results
        }

        if (source) {
          const params = {
            ...externalParams,
          }

          // Only add query parameter if there's a query or if we shouldn't omit empty queries
          if (query || !omitEmptyQuery) {
            params[queryParam] = query || ""
          }

          const {
            data: { items },
          } = await apiClient.get(source, { params })
          setOptions(items)
          return items
        }

        throw new Error("Autocomplete source must be defined")
      } catch (error) {
        if (typeof alerter === "function") {
          alerter("Failed to load options")
        } else if (alerter && typeof alerter.error === "function") {
          alerter.error("Failed to load options")
        }
        return []
      } finally {
        setLoading(false)
      }
    },
    [source, dynamicSource, externalParams, queryParam, omitEmptyQuery, defaultOptions, alerter, lastQuery]
  )

  // Load initial options when component mounts or when open and default options are enabled
  useEffect(() => {
    if (open && defaultOptions && options.length === 0 && !inputValue) {
      fetchOptions("")
    }
  }, [open, defaultOptions, fetchOptions, options.length, inputValue])

  // Create a memoized debounced version of fetchOptions
  const debouncedFetchOptions = useMemo(
    () =>
      debounce(query => {
        if (query !== lastQuery) {
          fetchOptions(query)
        }
      }, 300),
    [fetchOptions, lastQuery]
  )

  // Clean up the debounced function when component unmounts
  useEffect(() => {
    return () => {
      debouncedFetchOptions.cancel()
    }
  }, [debouncedFetchOptions])

  // Handle input change
  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue)
    if (newInputValue !== lastQuery) {
      debouncedFetchOptions(newInputValue)
    }
  }

  // Handle selection change
  const handleChange = (event, newValue) => {
    setValue(newValue)
    setInputValue("")
    setLastQuery(null)

    if (typeof onSelect === "function") {
      onSelect(newValue)
    }

    if (clearAfterSelect) {
      setValue(null)
    }

    if (formIdToAutoSubmit) {
      const form = document.forms[formIdToAutoSubmit]
      const input = document.querySelector(`[name=${fieldName}]`)

      if (form && input) {
        input.value = newValue ? newValue.value : ""
        form.submit()
      }
    }
  }

  return (
    <>
      <StyledAutocomplete
        multiple={isMulti}
        options={options}
        value={value}
        onChange={handleChange}
        onInputChange={handleInputChange}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        isOptionEqualToValue={(option, value) => {
          // Handle case where value is null or undefined
          if (!value) return false

          // Handle case where value might have items array
          if (value.items && Array.isArray(value.items) && value.items.length > 0) {
            return option.value === value.items[0].value
          }

          // Normal case - compare by value property
          return option.value === value.value
        }}
        getOptionLabel={option => option.title || ""}
        loading={loading}
        disabled={disabled}
        filterOptions={x => x} // Disable client-side filtering as we're doing server-side filtering
        className={className}
        hasvalue={value ? "true" : "false"}
        error={validationErrors ? "true" : undefined}
        renderInput={params => (
          <TextField
            {...params}
            placeholder={placeholder}
            variant="outlined"
            fullWidth
            error={Boolean(validationErrors)}
            sx={{
              borderRadius: "9999px",
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: showAdornment ? (
                <InputAdornment position="start">
                  {startAdornment || <SearchIcon sx={{ color: "action.active" }} />}
                </InputAdornment>
              ) : null,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {fieldName &&
        isMulti &&
        value?.map(({ value }, idx) => (
          <input key={idx} type="checkbox" checked hidden readOnly name={fieldName} value={value} />
        ))}

      {fieldName && !isMulti && <input type="hidden" name={fieldName} value={(value && value.value) || ""} />}

      {!hideError && <FormFieldErrorFeedback message={validationErrors} />}
    </>
  )
}

MuiAsyncAutocomplete.propTypes = {
  source: PropTypes.string,
  dynamicSource: PropTypes.func,
  placeholder: PropTypes.string,
  fieldName: PropTypes.string,
  selected: PropTypes.oneOfType([
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string,
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        title: PropTypes.string,
      })
    ),
  ]),
  externalParams: PropTypes.object,
  validationErrors: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  onSelect: PropTypes.func,
  formIdToAutoSubmit: PropTypes.string,
  hideError: PropTypes.bool,
  isMulti: PropTypes.bool,
  clearAfterSelect: PropTypes.bool,
  defaultOptions: PropTypes.bool,
  queryParam: PropTypes.string,
  omitEmptyQuery: PropTypes.bool,
  startAdornment: PropTypes.node,
  showAdornment: PropTypes.bool,
  alerter: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      error: PropTypes.func.isRequired,
    }),
  ]).isRequired,
}

export default withAlerter(MuiAsyncAutocomplete)
